/* Home dashboard — data layer: the searchable page registry, the widget catalogue,
 * and small hooks that wire each region to real data where a hook exists (reports,
 * groups, documents, events, directory) and fall back to typed placeholder shapes
 * with a TODO where the source needs new backend/query work. */
import { Icon } from "react-bootstrap-icons";
import {
  BarChartFill,
  CalendarWeek,
  CardChecklist,
  Diagram3,
  Droplet,
  Easel,
  FileEarmarkText,
  GeoAlt,
  Map as MapIcon,
  Mic,
  PeopleFill,
  PersonFill,
  Speaker,
} from "react-bootstrap-icons";
import { TranslationKey } from "../../helpers/i18n";

export type SearchKind = "page" | "person" | "document";

export type SearchItem = {
  label: string;
  kind: SearchKind;
  area: string;
  icon: Icon;
  to: string; // internal route, or external url for documents
  external?: boolean;
};

export type WidgetId =
  | "reports"
  | "assignments"
  | "week-meetings"
  | "field-service"
  | "groups"
  | "mm-upcoming"
  | "wm-upcoming"
  | "av"
  | "cleaning"
  | "custom"
  | "territory-due"
  | "recent-activity";

export type WidgetDef = {
  id: WidgetId;
  titleKey: TranslationKey;
  titleDefault: string;
  icon: Icon;
  span: 1 | 2;
};

export type ActionTone = "warn" | "danger" | "info";
export type ActionItem = {
  icon: Icon;
  label: string;
  to: string;
  count?: number;
  meta?: string;
  tag: string;
  tone: ActionTone;
};

export const PERSON_ICON: Icon = PersonFill;
export const DOCUMENT_ICON: Icon = FileEarmarkText;

/* ---------------- Widget catalogue ---------------- */
// NOTE: the "assignments" and "recent-activity" widgets are temporarily disabled (razor-1 review on
// #2572) — they're left out of the catalogue and the default layout so they neither render by default
// nor can be re-added, and isKnownWidget() sanitises them out of any persisted layout. Their
// components (and ASSIGNMENT_LINKS) are kept below so re-enabling is just restoring their entries here
// and in DEFAULT_LAYOUT.
// Widget titles reuse the same translation keys the nav/scheduling pages use (navRegistry labelKeys),
// so no new title assets are needed.
export const WIDGET_CATALOG: WidgetDef[] = [
  { id: "reports", titleKey: "nav.reports.0", titleDefault: "Reports", icon: BarChartFill, span: 1 },
  { id: "week-meetings", titleKey: "nav.meetings", titleDefault: "Meetings", icon: CalendarWeek, span: 2 },
  {
    id: "mm-upcoming",
    titleKey: "conganalysis.attendance.midweek",
    titleDefault: "Midweek Meeting",
    icon: Easel,
    span: 1,
  },
  {
    id: "wm-upcoming",
    titleKey: "conganalysis.attendance.weekend",
    titleDefault: "Weekend Meeting",
    icon: Mic,
    span: 1,
  },
  { id: "av", titleKey: "schedules.ava.title", titleDefault: "A/V & Attendant", icon: Speaker, span: 1 },
  { id: "cleaning", titleKey: "schedules.cleaning.title", titleDefault: "Cleaning", icon: Droplet, span: 1 },
  { id: "custom", titleKey: "general.custom", titleDefault: "Custom", icon: CardChecklist, span: 1 },
  { id: "territory-due", titleKey: "schedules.territory.territory", titleDefault: "Territory", icon: MapIcon, span: 1 },
  {
    id: "field-service",
    titleKey: "schedules.field-service.title",
    titleDefault: "Field Service Meeting",
    icon: GeoAlt,
    span: 1,
  },
  {
    id: "groups",
    titleKey: "nav.congregation.fsgroups",
    titleDefault: "Field Service Groups",
    icon: PeopleFill,
    span: 1,
  },
];

export const DEFAULT_LAYOUT: WidgetId[] = [
  "reports",
  "week-meetings",
  "mm-upcoming",
  "wm-upcoming",
  "av",
  "cleaning",
  "custom",
  "territory-due",
  "field-service",
  "groups",
];

export const LAYOUT_LS_KEY = "hg-home-layout-v1";

/** collapsed/expanded state of the rail's "What's new" section, remembered per device */
export const WHATSNEW_COLLAPSED_LS_KEY = "hg-home-whatsnew-collapsed-v1";

/** most recent announcements shown in the rail; the "more" link opens the full list */
export const WHATSNEW_RAIL_MAX = 4;

export type LayoutItem = { id: WidgetId; span: 1 | 2 };

export function widgetById(id: WidgetId): WidgetDef {
  return WIDGET_CATALOG.find((w) => w.id === id) ?? (WIDGET_CATALOG[0] as WidgetDef);
}

/** True for widget ids still present in the catalogue — used to sanitise persisted layouts so a
 * removed widget (e.g. the old "next-appointments" tile) doesn't render a broken card. */
export function isKnownWidget(id: string): id is WidgetId {
  return WIDGET_CATALOG.some((w) => w.id === id);
}

/* ---------------- Assignment shortcut links (Assignments widget) ---------------- */
export const ASSIGNMENT_LINKS: { key: TranslationKey; label: string; icon: Icon; to: string }[] = [
  { key: "conganalysis.attendance.midweek", label: "Life and Ministry Meeting", icon: Easel, to: "/scheduling/mm" },
  { key: "schedules.privileges.public-talks", label: "Public Talks", icon: Mic, to: "/scheduling/wm" },
  { key: "nav.congregation.fsgroups", label: "Field Service Groups", icon: Diagram3, to: "/manageGroups" },
  { key: "schedules.cleaning.title", label: "Cleaning schedule", icon: Droplet, to: "/scheduling/cleaning" },
  { key: "schedules.territory.territories", label: "Territories", icon: MapIcon, to: "/congregation/territory" },
  { key: "schedules.ava.title", label: "Sound and stage", icon: Speaker, to: "/scheduling/avattendant" },
  { key: "general.custom", label: "Custom assignments", icon: CalendarWeek, to: "/scheduling/custom" },
];
